<template>
  <div>
    <DatePicker
      :model-value="range"
      :timezone="timezone"
      :min-date="minDate"
      :max-date="maxDate"
      :disabled-dates="disabledDates"
      :unavailable-days="unavailableDays"
      :one-column="oneColumn"
      @date-click="onDateSelection"
    />

    <slot :dates="{ start: start ? formatAsDateISO(start) : null, end: end ? formatAsDateISO(end) : null }" />

    <TButton
      v-if="shouldShowClearButton"
      variant="link"
      size="sm"
      class="mt-1"
      @click="clearDates"
    >
      {{ t('clearDates') }}
    </TButton>
  </div>
</template>

<script setup lang="ts">
import type { Dayjs } from 'dayjs'

import type { DatesInstance } from '~/utils/datetime'
import type { BlockedRange, DaysOfWeek } from '~/types'
import type { SearchDates } from '~/types/search'

const props = defineProps<{
  dates: SearchDates
  timezone?: string
  disabledDates?: BlockedRange[]
  unavailableDays?: DaysOfWeek[]
  minDate?: Dayjs
  maxDate?: Dayjs
  showClearButton?: boolean
  freezeStartDate?: boolean
  oneColumn?: boolean
}>()

const emit = defineEmits<{
  'update:dates': [value: SearchDates]
  'loaded': []
}>()

const { t } = useI18n()
const { formatAsDateISO } = useDates()

const start = ref(props.dates.start ? datetime.utc(props.dates.start).set('hours', 12) : null)
const end = ref(props.dates.end ? datetime.utc(props.dates.end).set('hours', 12) : null)

const range = computed(() => {
  if (start.value && end.value) {
    return [start.value, end.value]
  }
  return start.value ?? end.value ?? undefined
})

const { hasDateRange } = useBookingEstimate()
const shouldShowClearButton = computed(() => props.showClearButton && hasDateRange.value)

watch(() => props.dates, (newDates) => {
  start.value = newDates?.start ? datetime.utc(newDates.start).set('hours', 12) : null
  end.value = newDates?.end ? datetime.utc(newDates.end).set('hours', 12) : null
}, {
  deep: true,
})

function onDateSelection(date: DatesInstance) {
  if (props.freezeStartDate && props.dates.start) {
    start.value = datetime.utc(props.dates.start).set('hours', 12)
    end.value = date
  }
  else if (!start.value || (start.value && end.value)) {
    start.value = date
    end.value = null
  }
  else {
    if (start.value.isSame(date, 'day')) {
      return
    }
    end.value = date

    ;[start.value = null, end.value = null] = sortDateRange([start.value, end.value])
  }

  emit('update:dates', {
    start: start.value ? formatAsDateISO(start.value) : undefined,
    end: end.value ? formatAsDateISO(end.value) : undefined,
  })
}

function clearDates() {
  start.value = null
  end.value = null
  emit('update:dates', { start: undefined, end: undefined })
}
</script>

<i18n lang="json">
{
  "en": {
    "clearDates": "Clear dates"
  },
  "fr": {
    "clearDates": "Réinitialiser les dates"
  }
}
</i18n>
